import { useTranslation } from 'next-i18next';
import React, { FC, useMemo } from 'react';
import { formatNumericValue } from 'utils/general';

import CardContent from '@/modules/Settlements/components/Details/CrewLevel/MainPayablesDays/CardContent';
import InstancesTable from '@/modules/Settlements/components/Details/CrewLevel/MainPayablesDays/InstancesTable';
import { getSerializedPeriodDaysArr } from '@/modules/Settlements/components/Details/CrewLevel/MainPayablesDays/logic';
import { EmptyDayCard } from '@/modules/Settlements/components/Details/shared/EmptyDayCard';
import { ISettlements } from '@/modules/Settlements/types';
import { Typography } from '@/shared/components/atoms';
import Collapse from '@/shared/components/atoms/Collapse';
import { useStore } from '@/shared/hooks/context/createGenericContext';

interface Props {
  data?: ISettlements.IGetSettlementDetails;
}
const MainPayablesDays: FC<Props> = ({ data }) => {
  const { t } = useTranslation('common');
  const [store] = useStore((store) => store);
  const selectedAsset = store?.asset;
  const isDemoFarm = store?.isDemoFarm;

  // add emptyDate if there are no information about this date in period from start and end date
  // return new days with injected empty record days
  // NOTE:: days must be sorted form backend side
  const serializedPeriodDaysArr = useMemo(
    () =>
      getSerializedPeriodDaysArr(
        data?.days ?? [],
        data?.startDate,
        data?.endDate
      ),
    [data]
  );

  return (
    <div className='mt-32'>
      <Typography className='text-black' weight='bold' variant='body1'>
        {t('dailyDuesDetails')}
      </Typography>

      <div className='mt-16 rounded-xl bg-neutralVariant-95 p-16'>
        <div className='flex flex-col gap-y-16'>
          {serializedPeriodDaysArr.map((data, index) =>
            // embty date
            data?.embtyDay ? (
              <EmptyDayCard key={index} day={data.embtyDay} />
            ) : (
              // date had issuance
              data?.day && (
                <Collapse
                  key={data?.day}
                  header={<CardContent data={data} />}
                  childrenPadding='px-0 py-16'
                  bgColor='bg-white'
                  containerPadding='px-16'
                  open={isDemoFarm ? false : undefined}
                  itemClassName={isDemoFarm ? 'cursor-default' : undefined}
                >
                  <InstancesTable data={data.instances} />
                </Collapse>
              )
            )
          )}
        </div>
        {/* total dues footer */}
        <div className='my-24 text-end'>
          <Typography className='mb-4' variant='body1'>
            {t('totalDailyDuesDetails')}
          </Typography>
          <Typography variant='h3' weight='bold'>
            {data?.totalDaysAmount
              ? `${formatNumericValue(data.totalDaysAmount)} ${
                  selectedAsset?.currency
                }`
              : '-'}
          </Typography>
        </div>
      </div>
    </div>
  );
};
export { MainPayablesDays };
